import moment from 'moment';
import { Spinner } from '../../../components/common';
import { convertObjectToParams } from '../../../components/common/create-params';
import {
  useCallOutcomesReport,
  CallOutcomesReport,
  CallFailReason,
  CallOutcome,
} from '../../../hooks/useCallReporting';
import { ROUTE_PATH } from '../../../routes/paths';
import { CallReportFilters } from './common/call-report-constants';

const callFailReasonMap: Record<CallFailReason, string> = {
  member_cancelled: 'Member Cancelled',
  member_rescheduled: 'Member Rescheduled',
  member_missed: 'Member Missed Call',
  member_voicemail: 'Member Voicemail',
  listener_missed: 'Listener Missed Call',
  listener_passed: 'Listener Passed Call',
  listener_timeout: 'Listener Timeout',
  listener_voicemail: 'Listener Voicemail',
  short_call: 'Short Call',
  unknown: 'Unknown',
};

const callOutcomeMap: Record<CallOutcome, string> = {
  success: 'Success',
  member_failed: 'Member Failed',
  listener_failed: 'Listener Failed',
};

interface CallDetailsProps {
  filters: CallReportFilters;
}

export const CallsDetails: React.FC<CallDetailsProps> = ({ filters }) => {
  const { data: callOutcomesReport, isLoading } = useCallOutcomesReport(filters);
  const formatOutcome = (outcome: CallOutcome): string => {
    return callOutcomeMap[outcome] || 'Unknown';
  };

  const formatFailReason = (failReason: CallFailReason): string => {
    return callFailReasonMap[failReason] || 'Unknown';
  };

  const formatCallDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
    const mins = Math.floor((seconds % 3600) / 60).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    const secs = (seconds % 60).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
    return `${hrs}:${mins}:${secs}`;
  };

  return (
    <div className="p-4">
      <h1 className="mb-4 text-2xl font-bold">Calls Details Report</h1>
      {isLoading && <Spinner />}
      {callOutcomesReport?.data.map((call: CallOutcomesReport) => (
        <div
          key={call.call_id_fmt}
          className="text-gray-dark flex min-h-48 w-full justify-between space-x-2 space-y-6 rounded border-b-8 border-white bg-gray-200 text-left"
        >
          <div className="w-1/4 select-none space-y-1 pl-6 pt-6">
            <p className="text-xs uppercase text-neutral-600">Call ID</p>
            <p className="text-base font-bold text-black">{call.call_id_fmt}</p>
            <p className="text-xs uppercase text-neutral-600">Call Type</p>
            <p className="text-base font-bold text-black">{call.is_scheduled_fmt}</p>
            <p className="text-xs uppercase text-neutral-600">Call Start</p>
            <div className="flex items-end space-x-2 whitespace-nowrap">
              <p className="text-base font-bold text-black">
                {moment.utc(call.call_start).local().format('MMM DD, YYYY HH:mm:ss')}
              </p>
              <p className="text-base text-neutral-600">({moment.tz(moment.tz.guess()).format('z')})</p>
            </div>
          </div>
          <div className="w-1/4 space-y-1">
            <p className="select-none text-xs uppercase text-neutral-600">Client</p>
            <p
              className="cursor-pointer text-base font-bold text-black"
              onClick={() => {
                localStorage.setItem('clientid', call.client_id.toString());
                window.open(ROUTE_PATH.REPORTING, '_blank');
              }}
            >
              {call.client_name}
            </p>
            <p className="select-none text-xs uppercase text-neutral-600">Member</p>
            <p
              className="cursor-pointer text-base font-bold text-black"
              onClick={() => {
                window.open(
                  ROUTE_PATH.MEMBERS_PROFILE +
                    convertObjectToParams({
                      callerRoleId: call.caller_role_id,
                    }),
                  '_blank',
                );
              }}
            >
              {call.member_name}
            </p>
            <p className="select-none text-xs uppercase text-neutral-600">Listener</p>
            <p
              className="cursor-pointer text-base font-bold text-black"
              onClick={() => {
                window.open(
                  ROUTE_PATH.LISTENERS_PROFILE +
                    convertObjectToParams({
                      listenerRoleId: call.listener_role_id,
                      listenerId: call.listener_role.user.id,
                    }),
                  '_blank',
                );
              }}
            >
              {call.listener_name}
            </p>
          </div>
          <div className="w-1/4 select-none space-y-1">
            <p className="text-xs uppercase text-neutral-600">Outcome</p>
            <div className="flex">
              <p
                className={`px-1 text-base font-bold text-white ${call.outcome === 'success' ? 'bg-green' : 'bg-red'} w-content flex rounded-sm`}
              >
                {formatOutcome(call.outcome as CallOutcome)}
              </p>
            </div>
            {call.outcome !== 'success' ? (
              <>
                <p className="text-xs uppercase text-neutral-600">Failure Reason</p>
                <p className="text-base font-bold text-black">{formatFailReason(call.fail_reason as CallFailReason)}</p>
              </>
            ) : (
              <div className="h-11"></div>
            )}
            <p className="text-xs uppercase text-neutral-600">Crisis</p>
            <p className="text-base font-bold text-black">{call.is_crisis_fmt}</p>
          </div>
          <div className="w-1/4 space-y-1 pr-1">
            <p className="text-xs uppercase text-neutral-600">Duration</p>
            <p className="text-base font-bold text-black">{formatCallDuration(call.duration)}</p>
            <p className="text-xs uppercase text-neutral-600">Member Call Topics</p>
            <p className="flex flex-wrap gap-1 text-base text-black">
              {call.member_tag_groups ? (
                call.member_tag_groups.map((tag, index) => {
                  return (
                    <div
                      key={index}
                      className="bg-blue-primary w-content whitespace-nowrap rounded-md px-1 py-0.5 text-sm font-bold text-white"
                    >
                      {tag}
                    </div>
                  );
                })
              ) : (
                <p className="text-base font-bold text-black">N/A</p>
              )}
            </p>
            <p className="text-xs uppercase text-neutral-600">Listener Call Topics</p>
            <p className="flex flex-wrap gap-1 pb-4 text-base text-black">
              {call.listener_topic_tags ? (
                call.listener_topic_tags.map((tag, index) => {
                  return (
                    <div
                      key={index}
                      className="bg-blue-primary w-content whitespace-nowrap rounded-md px-1 py-0.5 text-sm font-bold text-white"
                    >
                      {tag}
                    </div>
                  );
                })
              ) : (
                <p className="text-base font-bold text-black">N/A</p>
              )}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};
